import api from "@/lib/api";
import type { CategoryItem } from "../types/index";
import type { StrapiResponse } from "@/types";

export function mapCategoryItem(raw: unknown): CategoryItem {
  if (!raw || typeof raw !== "object") {
    return {
      id: 0,
      documentId: "",
      name: "",
      description: "",
      slug: "",
      createdAt: "",
      updatedAt: "",
      publishedAt: "",
    };
  }

  const obj = raw as Record<string, unknown>;
  return {
    ...obj,
    id: typeof obj.id === "number" ? obj.id : 0,
    documentId: typeof obj.documentId === "string" ? obj.documentId : "",
    name: typeof obj.name === "string" ? obj.name : "",
    slug: typeof obj.slug === "string" ? obj.slug : "",
    description: typeof obj.description === "string" ? obj.description : "",
    createdAt: typeof obj.createdAt === "string" ? obj.createdAt : "",
    updatedAt: typeof obj.updatedAt === "string" ? obj.updatedAt : "",
    publishedAt: typeof obj.publishedAt === "string" ? obj.publishedAt : "",
  };
}

export const categoryService = {
  /**
   * Fetches all categories from the API
   * @returns Promise<CategoryItem[]> - Array of category items
   */
  async getCategories(): Promise<CategoryItem[]> {
    const response = await api.get<StrapiResponse<CategoryItem[]>>(
      "/categories",
      {
        params: {
          populate: ["image", "products"],
        },
      },
    );

    const categories = response.data.data;
    return Array.isArray(categories) ? categories.map(mapCategoryItem) : [];
  },

  /**
   * Fetches a specific category by its document ID
   * @param documentId - The document ID of the category to fetch
   * @returns Promise<CategoryItem | null> - The category item or null if not found
   */
  async getCategoryById(documentId: string): Promise<CategoryItem | null> {
    try {
      if (!documentId || typeof documentId !== "string") {
        return null;
      }

      const response = await api.get<StrapiResponse<CategoryItem>>(
        `/categories/${documentId}`,
        {
          params: {
            populate: "image",
          },
        },
      );

      return response.data.data ? mapCategoryItem(response.data.data) : null;
    } catch {
      return null;
    }
  },
};
